import { useSyncExternalStore } from 'react';
import type { RemiseTier } from '@erp/domain/pro';

// ─────────────────────────────────────────────────────────────────────
// Préférences UI locales du pro (par appareil, localStorage).
//
// Source unique partagée entre la page Paramètres et le panier : le même
// objet est lu/écrit par les deux, et `useProPrefs` re-rend tout composant
// abonné quand une préférence change (même onglet via un event custom,
// autres onglets via l'event `storage` natif).
//
// NB : la base de prix par défaut (defaultRemiseTier) vit côté serveur sur
// pro_customers — le champ homonyme conservé ici n'est qu'un héritage lu en
// dernier recours par le panier (initialTier) pour d'anciens appareils.
// ─────────────────────────────────────────────────────────────────────

const PREFS_KEY = 'pro_user_prefs_v1';
const PREFS_EVENT = 'pro-user-prefs-changed';

export interface ProUserPrefs {
  notifGeneral: boolean;
  notifEmail: boolean;
  notifWhatsapp: boolean;
  defaultRemiseTier: RemiseTier;
  /** Afficher la commission du pro (panier + validation de commande).
   *  Visible par défaut ; le pro peut la masquer depuis Paramètres. */
  showCommission: boolean;
}

export const DEFAULT_PRO_PREFS: ProUserPrefs = {
  notifGeneral: true,
  notifEmail: true,
  notifWhatsapp: false,
  defaultRemiseTier: 'moy',
  showCommission: true,
};

export function readProPrefs(): ProUserPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PRO_PREFS;
    return { ...DEFAULT_PRO_PREFS, ...(JSON.parse(raw) as Partial<ProUserPrefs>) };
  } catch {
    return DEFAULT_PRO_PREFS;
  }
}

export function writeProPrefs(patch: Partial<ProUserPrefs>): ProUserPrefs {
  const next = { ...readProPrefs(), ...patch };
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode — les prefs restent en mémoire pour ce rendu */
  }
  // Invalide le snapshot AVANT de notifier, puis re-rend les abonnés du
  // même onglet (l'event `storage` natif ne couvre que les AUTRES onglets).
  cachedSnapshot = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PREFS_EVENT));
  }
  return next;
}

// Snapshot mémoïsé : useSyncExternalStore exige une référence stable tant que
// rien n'a changé (sinon boucle de re-rendus).
let cachedSnapshot: ProUserPrefs | null = null;

function getSnapshot(): ProUserPrefs {
  cachedSnapshot ??= readProPrefs();
  return cachedSnapshot;
}

function subscribe(onChange: () => void): () => void {
  const invalidate = () => {
    cachedSnapshot = null;
    onChange();
  };
  window.addEventListener(PREFS_EVENT, invalidate);
  window.addEventListener('storage', invalidate);
  return () => {
    window.removeEventListener(PREFS_EVENT, invalidate);
    window.removeEventListener('storage', invalidate);
  };
}

/** Préférences réactives — re-rend le composant quand une pref change. */
export function useProPrefs(): ProUserPrefs {
  return useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_PRO_PREFS);
}
